import React, {Fragment} from 'react';

import {translations} from 'presentation/translations-new';
import {useTelehealthProfessionalSelection} from './hooks/useTelehealthProfessionalSelection';
import {TelehealthProfessionalSelectionProps} from './types';
import {BookingLoadingState} from 'presentation/modules/booking/shared/ui/components/BookingLoadingState';
import {BookingErrorState} from 'presentation/modules/booking/shared/ui/components/BookingErrorState';
import {TelehealthProfessionalSelectionNotFoundState} from './components/TelehealthProfessionalSelectionNotFoundState';
import {FinishWithoutCompleteFlowEnum} from 'main/analytics/gaTagsEnum';
import {TelehealthSelectedDateProfessionalList} from './components/TelehealthSelectedDateProfessionalList';
import {TelehealthNearestDateProfessionalList} from './components/TelehealthNearestDateProfessionalList';
import {TherapyAlert} from 'presentation/modules/booking/shared/ui/components/TherapyAlert';
import {AlertModal} from 'presentation/components';
import {PendingDialog} from 'presentation/modules/booking/shared/ui/components/PendingDialog';
import {FlowEnum} from 'presentation/modules/booking/shared/constants/flowEnum';

const {bookTelehealth} = translations['pt-br'].privateFlow.services;
const {loadingState, loadingError, verifyingAuth, errorModal} =
	translations['pt-br'].privateFlow.bookingFlow.professionalFlow;

export function TelehealthProfessionalSelection({
	handleNextStep,
}: TelehealthProfessionalSelectionProps) {
	const {
		refetch,
		isLoading,
		isError,
		showNotFoundState,
		handleSelectHour,
		showSelectedDateProfessionalGrid,
		handleClearShiftAndSearch,
		availableDates,
		filteredProvidersNameOptions,
		authModal,
		setAuthModal,
		setExternalOpenDateModal,
	} = useTelehealthProfessionalSelection({handleNextStep});

	if (isLoading) {
		return (
			<BookingLoadingState
				showHeader={false}
				pageTitle={bookTelehealth}
				loadingText={loadingState}
			/>
		);
	}

	if (isError) {
		return (
			<BookingErrorState
				showHeader={false}
				pageTitle={bookTelehealth}
				errorTitle={loadingError}
				handleRefresh={refetch}
			/>
		);
	}

	if (showNotFoundState) {
		return (
			<TelehealthProfessionalSelectionNotFoundState
				dataTagId={FinishWithoutCompleteFlowEnum.SEM_PROFISSIONAL_DISPONIVEL}
			/>
		);
	}

	return (
		<Fragment>
			{showSelectedDateProfessionalGrid ? (
				<TelehealthSelectedDateProfessionalList
					handleSelectHour={handleSelectHour}
					handleClearShiftAndSearch={handleClearShiftAndSearch}
					filteredProvidersNameOptions={filteredProvidersNameOptions}
				/>
			) : (
				<TelehealthNearestDateProfessionalList
					handleSelectHour={handleSelectHour}
					handleClearShiftAndSearch={handleClearShiftAndSearch}
					availableDates={availableDates ?? []}
					filteredProvidersNameOptions={filteredProvidersNameOptions}
				/>
			)}

			<TherapyAlert
				flowType={FlowEnum.TELEHEALTH}
				alertType="allScheduled"
				open={authModal === 'allScheduled'}
				setOpen={() => setAuthModal('none')}
			/>

			<TherapyAlert
				flowType={FlowEnum.TELEHEALTH}
				alertType="needAuth"
				open={authModal === 'needAth'}
				setOpen={() => setAuthModal('none')}
			/>

			<TherapyAlert
				flowType={FlowEnum.TELEHEALTH}
				alertType="minimumInterval"
				open={authModal === 'minimumInterval'}
				setOpen={() => setAuthModal('none')}
				setCalendarOpen={() => setExternalOpenDateModal(true)}
			/>

			<AlertModal
				headerTitle={errorModal.headerTitle}
				title={errorModal.title}
				variant="error"
				description={errorModal.description}
				open={authModal === 'error'}
				onClose={() => setAuthModal('none')}
			/>

			<PendingDialog
				open={authModal === 'loading'}
				onClose={() => setAuthModal('none')}
				text={verifyingAuth}
			/>
		</Fragment>
	);
}
